import { Badge, Button, Col, CreateTableState, Grid, Icon, Tooltip, Typography } from 'gu-ui-kit'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useTranslation } from 'react-i18next'
import styles from 'modules/Finance/PromisedPayment/PromisedPayment.module.scss'
import format from 'utils/dateFns/format'
import formatMoney from 'utils/formatMoney'
import { STATUSES } from 'modules/Finance/PromisedPayment/config/consts'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'

export const useTableConfig = ({ data = [] }) => {
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const [columnVisibility, setColumnVisibility] = useState()

  useEffect(() => {
    if (breakpoint.mdl) {
      setColumnVisibility({
        mobile: false,
        created_at: true,
        name: true,
        status: true,
        amount: true,
        repayment_amount: true,
        repayment_date: true,
        action: true,
      })
    } else if (breakpoint.lg) {
      setColumnVisibility({
        mobile: false,
        created_at: true,
        name: true,
        status: false,
        amount: true,
        repayment_amount: true,
        repayment_date: true,
        action: true,
      })
    } else if (breakpoint.fb) {
      setColumnVisibility({
        mobile: false,
        created_at: false,
        name: true,
        status: false,
        amount: true,
        repayment_amount: true,
        repayment_date: false,
        action: true,
      })
    } else {
      setColumnVisibility({
        mobile: true,
        created_at: false,
        name: false,
        status: false,
        amount: false,
        repayment_amount: false,
        repayment_date: false,
        action: false,
      })
    }
  }, [breakpoint])

  const tableConfig = {
    columns: [
      {
        id: 'mobile',
        header: '',
        cell: ({
          row: {
            original: {
              name,
              created_at,
              wallet: { name: walletName = null } = {},
              status,
              is_expired,
              amount,
              currency,
              repayment_amount,
              repayment_date,
            },
          },
        }) => {
          const { icon = null, color, ...rest } = STATUSES?.[is_expired ? 'expired' : status] ?? {}
          return (
            <Grid
              direction={'column'}
              alignItems={'start'}
              justifyContent={'start'}
              gap={16}
            >
              <Grid
                alignItems={'center'}
                justifyContent={'start'}
                cols={breakpoint.sm ? 'auto' : 2}
                gap={16}
                wrap={false}
              >
                <Typography
                  variant={'Body-15'}
                  component={'div'}
                  color={'Label-Fifth'}
                  className={styles['promised-payment__cell-mobile-title']}
                >
                  {t('promised_payment::table.head.created_at.label')}
                </Typography>
                <Typography
                  variant={'Body-15'}
                  color={'Label-Fifth'}
                  component={'div'}
                >
                  {format(created_at * 1000, 'dd.MM.yyyy')}
                </Typography>
              </Grid>
              <Grid
                alignItems={'center'}
                justifyContent={'start'}
                cols={breakpoint.sm ? 'auto' : 2}
                gap={16}
                wrap={false}
              >
                <Typography
                  variant={'Body-15'}
                  component={'div'}
                  color={'Label-Fifth'}
                  className={styles['promised-payment__cell-mobile-title']}
                >
                  {t('promised_payment::table.head.name.label')}
                </Typography>
                <Typography
                  variant={'Body-15'}
                  justifyContent={'center'}
                  color={'Label-Fifth'}
                >
                  {name || walletName || '-'}
                </Typography>
              </Grid>
              <Grid
                alignItems={'center'}
                justifyContent={'start'}
                cols={breakpoint.sm ? 'auto' : 2}
                gap={16}
                wrap={false}
              >
                <Typography
                  variant={'Body-15'}
                  component={'div'}
                  color={'Label-Fifth'}
                  className={styles['promised-payment__cell-mobile-title']}
                >
                  {t('promised_payment::table.head.status.label')}
                </Typography>
                <Col>
                  <Badge
                    {...rest}
                    size={'xs'}
                    color={color}
                    rounded
                    startIcon={
                      icon ? (
                        <Icon
                          size={'s'}
                          name={icon}
                          color={color}
                        />
                      ) : null
                    }
                    variant={'Body-10'}
                    noWrap
                  >
                    {t(`promised_payment::status.${is_expired ? 'expired' : status}`)}
                  </Badge>
                </Col>
              </Grid>
              <Grid
                alignItems={'center'}
                justifyContent={'start'}
                cols={breakpoint.sm ? 'auto' : 2}
                gap={16}
                wrap={false}
              >
                <Typography
                  variant={'Body-15'}
                  component={'div'}
                  color={'Label-Fifth'}
                  className={styles['promised-payment__cell-mobile-title']}
                >
                  {t('promised_payment::table.head.amount.label')}
                </Typography>
                <Typography
                  variant={'Body-15'}
                  color={'Label-Fifth'}
                  dangerouslySetInnerHTML={{ __html: formatMoney(amount, currency) }}
                />
              </Grid>
              <Grid
                alignItems={'center'}
                justifyContent={'start'}
                cols={breakpoint.sm ? 'auto' : 2}
                gap={16}
                wrap={false}
              >
                <Grid
                  alignItems={'center'}
                  gap={8}
                  wrap={false}
                  className={styles['promised-payment__cell-mobile-title']}
                >
                  <Typography
                    variant={'Body-15'}
                    component={'div'}
                    color={'Label-Fifth'}
                  >
                    {t('promised_payment::table.head.repayment_amount.label')}
                  </Typography>
                  <Tooltip title={t('promised_payment::table.head.repayment_amount.tooltip')}>
                    <Icon
                      name={'information'}
                      color={'Interface-Seventh'}
                      width={20}
                    />
                  </Tooltip>
                </Grid>
                <Typography
                  variant={'Body-15'}
                  color={'Label-Fifth'}
                  dangerouslySetInnerHTML={{ __html: formatMoney(repayment_amount, currency) }}
                />
              </Grid>

              <Grid
                alignItems={'center'}
                justifyContent={'start'}
                cols={breakpoint.sm ? 'auto' : 2}
                gap={16}
                wrap={false}
              >
                <Typography
                  variant={'Body-15'}
                  component={'div'}
                  color={'Label-Fifth'}
                  className={styles['promised-payment__cell-mobile-title']}
                >
                  {t('promised_payment::table.head.repayment_date.label')}
                </Typography>
                {repayment_date ? (
                  <Typography
                    variant={'Body-15'}
                    color={'Label-Fifth'}
                  >
                    {format(repayment_date * 1000, 'dd.MM.yyyy')}
                  </Typography>
                ) : (
                  '-'
                )}
              </Grid>
              {is_expired ? (
                <Button
                  size={'s'}
                  variant={'primary'}
                  component={Link}
                  to={pathGen(ROUTES.BILLING_TOP_UP)}
                >
                  {t('common::buttons.pay')}
                </Button>
              ) : null}
            </Grid>
          )
        },

        className: clsx(styles['promised-payment__cell'], styles['promised-payment__cell_mobile']),
      },
      {
        id: 'created_at',
        header: t('promised_payment::table.head.created_at.label'),
        cell: ({
          row: {
            original: { created_at },
          },
        }) => (
          <Typography
            variant={'Body-15'}
            color={'Label-Fifth'}
          >
            {format(created_at * 1000, 'dd.MM.yyyy')}
          </Typography>
        ),

        className: clsx(
          styles['promised-payment__cell'],
          styles['promised-payment__cell_created-at'],
        ),
      },
      {
        id: 'status',
        header: t('promised_payment::table.head.status.label'),
        className: clsx(styles['promised-payment__cell'], styles['promised-payment__cell_status']),
        cell: ({
          row: {
            original: { status, is_expired = false },
          },
        }) => {
          const { icon = null, color, ...rest } = STATUSES?.[is_expired ? 'expired' : status] ?? {}
          return (
            <Badge
              {...rest}
              size={'xs'}
              color={color}
              rounded
              startIcon={
                icon ? (
                  <Icon
                    size={'s'}
                    name={icon}
                    color={color}
                  />
                ) : null
              }
              variant={'Body-10'}
              noWrap
            >
              {t(`promised_payment::status.${is_expired ? 'expired' : status}`)}
            </Badge>
          )
        },
      },
      {
        id: 'name',
        header: t('promised_payment::table.head.name.label'),
        className: clsx(styles['promised-payment__cell'], styles['promised-payment__cell_name']),
        cell: (props) => {
          const {
            row: {
              original: {
                name,
                wallet: { name: walletName = null } = {},
                created_at,
                repayment_date,
                is_expired,
                status,
              },
            },
          } = props
          const { icon = null, color, ...rest } = STATUSES?.[is_expired ? 'expired' : status] ?? {}
          return (
            <>
              <Typography
                variant={'Body-15'}
                justifyContent={'center'}
                color={'Label-Fifth'}
                component={'div'}
              >
                {name || walletName || '-'}
              </Typography>
              {breakpoint.lg && !breakpoint.mdl && (
                <Badge
                  className={'margin-top-8'}
                  {...rest}
                  size={'xs'}
                  color={color}
                  rounded
                  startIcon={
                    icon ? (
                      <Icon
                        size={'s'}
                        name={icon}
                        color={color}
                      />
                    ) : null
                  }
                  variant={'Body-10'}
                  noWrap
                >
                  {t(`promised_payment::status.${is_expired ? 'expired' : status}`)}
                </Badge>
              )}
              
              {breakpoint.fb && !breakpoint.lg && (
                <>
                  <Grid
                    alignItems={'center'}
                    justifyContent={'start'}
                    cols={breakpoint.sm ? 'auto' : 2}
                    gap={16}
                    wrap={false}
                    className={'margin-top-8'}
                  >
                    <Typography
                      variant={'Body-15'}
                      component={'div'}
                      color={'Label-Fifth'}
                    >
                      {t('promised_payment::table.head.created_at.label')}:
                    </Typography>
                    <Typography
                      variant={'Body-15'}
                      color={'Label-Fifth'}
                      component={'div'}
                    >
                      {format(created_at * 1000, 'dd.MM.yyyy')}
                    </Typography>
                  </Grid>
                  <Grid
                    alignItems={'center'}
                    justifyContent={'start'}
                    cols={breakpoint.sm ? 'auto' : 2}
                    gap={16}
                    wrap={false}
                  >
                    <Typography
                      variant={'Body-15'}
                      component={'div'}
                      color={'Label-Fifth'}
                    >
                      {t('promised_payment::table.head.repayment_date.label')}:
                    </Typography>
                    {repayment_date ? (
                      <Typography
                        variant={'Body-15'}
                        color={'Label-Fifth'}
                      >
                        {format(repayment_date * 1000, 'dd.MM.yyyy')}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </Grid>
                  <div className={'margin-top-8'}>
                    <Badge
                      {...rest}
                      size={'xs'}
                      color={color}
                      noWrap
                      rounded
                      startIcon={
                        icon ? (
                          <Icon
                            size={'s'}
                            name={icon}
                            color={color}
                          />
                        ) : null
                      }
                      variant={'Body-10'}
                    >
                      {t(`promised_payment::status.${is_expired ? 'expired' : status}`)}
                    </Badge>
                  </div>
                </>
              )}
            </>
          )
        },
      },
      {
        id: 'amount',
        header: t('promised_payment::table.head.amount.label'),
        className: clsx(styles['promised-payment__cell'], styles['promised-payment__cell_amount']),
        cell: ({
          row: {
            original: { amount, currency },
          },
        }) => (
          <Typography
            variant={'Body-15'}
            color={'Label-Fifth'}
            dangerouslySetInnerHTML={{ __html: formatMoney(amount, currency) }}
          />
        ),
      },
      {
        id: 'repayment_amount',
        header: () => (
          <Grid
            alignItems={'center'}
            gap={8}
            wrap={false}
          >
            <Col>{t('promised_payment::table.head.repayment_amount.label')}</Col>
            <Tooltip title={t('promised_payment::table.head.repayment_amount.tooltip')}>
              <Icon
                name={'information'}
                color={'Interface-Seventh'}
                width={20}
              />
            </Tooltip>
          </Grid>
        ),
        isPlaceholder: true,
        className: clsx(
          styles['promised-payment__cell'],
          styles['promised-payment__cell_repayment_amount'],
        ),
        cell: ({
          row: {
            original: { repayment_amount, currency },
          },
        }) => (
          <Typography
            variant={'Body-15'}
            color={'Label-Fifth'}
            dangerouslySetInnerHTML={{ __html: formatMoney(repayment_amount, currency) }}
          />
        ),
      },
      {
        id: 'repayment_date',
        header: t('promised_payment::table.head.repayment_date.label'),
        isPlaceholder: true,
        className: clsx(
          styles['promised-payment__cell'],
          styles['promised-payment__cell_repayment_date'],
        ),
        cell: ({
          row: {
            original: { repayment_date },
          },
        }) =>
          repayment_date ? (
            <Typography
              variant={'Body-15'}
              color={'Label-Fifth'}
            >
              {format(repayment_date * 1000, 'dd.MM.yyyy')}
            </Typography>
          ) : (
            '-'
          ),
      },
      {
        id: 'action',
        header: ' ',
        isPlaceholder: true,
        className: clsx(styles['promised-payment__cell'], styles['promised-payment__cell_action']),
        cell: ({
          row: {
            original: { is_expired },
          },
        }) =>
          is_expired ? (
            <Button
              size={'s'}
              variant={'primary'}
              component={Link}
              to={pathGen(ROUTES.BILLING_TOP_UP)}
            >
              {t('common::buttons.pay')}
            </Button>
          ) : null,
      },
    ],
    data,
    state: {
      columnVisibility,
    },
    showHeader: breakpoint.fb && data.length > 0,
    showFooter: false,
    className: clsx(styles['promised-payment__table'], 'margin-bottom-24'),
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
